﻿import { useEffect, useState } from 'react';
import {
  Page, Card, Button, Banner, Text, Select,
  DataTable, EmptyState, Spinner, Layout, BlockStack,
  InlineStack, Box, Pagination, Tooltip, InlineGrid,
} from '@shopify/polaris';
import { StarIcon, EmailIcon, ListBulletedIcon } from '@shopify/polaris-icons';
import { useReviewRequest } from '../../hooks/useReviewRequest';
import type { ReviewRequestCampaign } from '../../api/reviewRequest';
import { StatCard } from '../shared/StatCard';
import { IconBadge } from '../shared/IconBadge';

interface DetailProps {
  campaign: ReviewRequestCampaign;
  onBack: () => void;
  onDelete: (id: number) => void;
}

function CampaignDetail({ campaign, onBack, onDelete }: DetailProps) {
  return (
    <Page fullWidth
      title={`Review Request Campaign (${campaign.min_days_since_order}+ days since order)`}
      backAction={{ content: 'All Campaigns', onAction: onBack }}
      secondaryActions={[{
        content: 'Delete',
        destructive: true,
        onAction: () => { onDelete(campaign.id); onBack(); },
      }]}
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
            <Card>
              <BlockStack gap="100">
                <Text as="p" variant="bodySm" tone="subdued">Orders analysed</Text>
                <Text as="p" variant="heading2xl" fontWeight="bold">{campaign.orders_analyzed}</Text>
              </BlockStack>
            </Card>

            {campaign.ai_summary && (
              <Card>
                <BlockStack gap="200">
                  <Text as="h2" variant="headingMd">Summary</Text>
                  <Text as="p" variant="bodyMd">{campaign.ai_summary}</Text>
                </BlockStack>
              </Card>
            )}

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Review Request Emails</Text>
                {campaign.ai_recommendations.length === 0 ? (
                  <Text as="p" tone="subdued">No recommendations generated.</Text>
                ) : (
                  <BlockStack gap="300">
                    {campaign.ai_recommendations.map((r, i) => (
                      <Card key={i} background="bg-surface-secondary">
                        <BlockStack gap="150">
                          <InlineStack gap="200" blockAlign="center" align="space-between">
                            <InlineStack gap="300" blockAlign="center" wrap={false}>
                              <IconBadge icon={StarIcon} color="orange" />
                              <Text as="h3" variant="headingSm">{r.customer_name}</Text>
                            </InlineStack>
                            <Tooltip content="Copy email body">
                              <Button
                                size="micro"
                                variant="plain"
                                onClick={() => navigator.clipboard.writeText(r.email_body)}
                              >
                                Copy email
                              </Button>
                            </Tooltip>
                          </InlineStack>
                          <Text as="p" variant="bodySm" fontWeight="semibold">
                            Product: {r.product_title}
                          </Text>
                          <Text as="p" variant="bodySm" tone="subdued">
                            Subject: {r.email_subject}
                          </Text>
                          <Text as="p" variant="bodyMd">{r.email_body}</Text>
                          <Text as="p" variant="bodySm" tone="subdued">{r.rationale}</Text>
                        </BlockStack>
                      </Card>
                    ))}
                  </BlockStack>
                )}
              </BlockStack>
            </Card>

            <Card>
              <BlockStack gap="400">
                <Text as="h2" variant="headingMd">Eligible Customers</Text>
                {campaign.candidates.length === 0 ? (
                  <Text as="p" tone="subdued">No candidates found.</Text>
                ) : (
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'numeric']}
                    headings={['Customer', 'Email', 'Product', 'Days Since Order']}
                    rows={campaign.candidates.map((c) => [
                      c.customer_name,
                      c.email ?? '—',
                      c.product_title,
                      String(c.days_since_order),
                    ])}
                  />
                )}
              </BlockStack>
            </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

const MIN_DAYS_OPTIONS = [
  { label: '3+ days since order', value: '3' },
  { label: '7+ days since order', value: '7' },
  { label: '14+ days since order', value: '14' },
  { label: '21+ days since order', value: '21' },
];

export function ReviewRequestPanel() {
  const {
    campaigns, activeCampaign, loading, generating, error,
    total, page, lastPage, fetchCampaigns, generate, deleteCampaign, setActiveCampaign, setError,
  } = useReviewRequest();

  const [minDaysSinceOrder, setMinDaysSinceOrder] = useState('7');

  useEffect(() => {
    fetchCampaigns(1);
  }, []);

  if (activeCampaign) {
    return (
      <CampaignDetail
        campaign={activeCampaign}
        onBack={() => setActiveCampaign(null)}
        onDelete={deleteCampaign}
      />
    );
  }

  const latest = campaigns[0];

  return (
    <Page fullWidth
      title="Review Request Automation"
      subtitle="Finds customers who bought but likely haven't left a review yet, and drafts a personalised post-purchase review-request email for each"
    >
      <Layout>
        <Layout.Section>
          <BlockStack gap="400">
          {error && (
            <Banner tone="critical" onDismiss={() => setError(null)}>
              <p>{error}</p>
            </Banner>
          )}

          {latest && (
            <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
              <StatCard label="Customers Ready for a Review Ask" value={String(latest.candidates.length)} icon={StarIcon} iconColor="orange" />
              <StatCard label="Emails Drafted" value={String(latest.ai_recommendations.length)} icon={EmailIcon} iconColor="blue" />
              <StatCard label="Campaigns Run" value={String(total)} icon={ListBulletedIcon} iconColor="indigo" />
            </InlineGrid>
          )}

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Generate New Campaign</Text>
              <Text as="p" variant="bodySm" tone="subdued">
                Scans recently fulfilled orders and drafts a warm, ready-to-send review-request email
                for each customer who's had time to receive their order.
              </Text>
              <InlineStack gap="300" blockAlign="end">
                <Box minWidth="220px">
                  <Select label="Minimum wait since order" options={MIN_DAYS_OPTIONS} value={minDaysSinceOrder} onChange={setMinDaysSinceOrder} />
                </Box>
                <Button
                  variant="primary"
                  loading={generating}
                  onClick={() => generate({ min_days_since_order: Number(minDaysSinceOrder) })}
                >
                  Generate Campaign
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">Past Campaigns ({total})</Text>

              {loading ? (
                <InlineStack align="center"><Spinner /></InlineStack>
              ) : campaigns.length === 0 ? (
                <EmptyState
                  heading="No review request campaigns yet"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                  action={{ content: 'Generate First Campaign', onAction: () => generate({ min_days_since_order: Number(minDaysSinceOrder) }) }}
                >
                  <p>Generate a campaign to find customers ready for a review request.</p>
                </EmptyState>
              ) : (
                <DataTable
                  columnContentTypes={['text', 'numeric', 'numeric', 'text']}
                  headings={['Minimum Wait', 'Orders Analysed', 'Recommendations', 'Actions']}
                  rows={campaigns.map((c) => [
                    <Button variant="plain" onClick={() => setActiveCampaign(c)}>{`${c.min_days_since_order}+ days`}</Button>,
                    String(c.orders_analyzed),
                    String(c.ai_recommendations.length),
                    <Button variant="plain" tone="critical" size="slim" onClick={() => deleteCampaign(c.id)}>Delete</Button>,
                  ])}
                />
              )}

              {lastPage > 1 && (
                <InlineStack align="center">
                  <Pagination
                    hasPrevious={page > 1}
                    onPrevious={() => fetchCampaigns(page - 1)}
                    hasNext={page < lastPage}
                    onNext={() => fetchCampaigns(page + 1)}
                  />
                </InlineStack>
              )}
            </BlockStack>
          </Card>
          </BlockStack>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
